import { useState } from "react";

import toast from "react-hot-toast";

import { getTenant } from "../../../utils/tenantManager";

import {
  getScalesByCustomerApi,
  deleteScaleApi,
  updateScaleNotesApi,
  createCalibrationReportsApi,
} from "../../../services/operation/scale/scaleService";

export const useCustomerScaleViewModel = () => {
  const tenantId = getTenant();

  const [scales, setScales] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const loadScales = async (customerId: string) => {
    try {
      setLoading(true);

      const res = await getScalesByCustomerApi(customerId, tenantId!);

      setScales(res?.data || []);
    } catch {
      toast.error("Failed to load scales");
    } finally {
      setLoading(false);
    }
  };

  const deleteScale = async (id: string, customerId: string) => {
    try {
      setLoading(true);

      await deleteScaleApi(id, tenantId!);

      await loadScales(customerId);
    } catch {
      toast.error("Failed to delete scale");
    } finally {
      setLoading(false);
    }
  };

  const clearScales = () => {
    setScales([]);
  };

  const updateNotes = async (
    id: string,
    data: {
      identifier: string;
      make: string;
      model: string;
      capacity: string;
      serialNumber: string;
      location: string;
      comment: string;
    },
    customerId: string,
  ) => {
    try {
      setLoading(true);

      await updateScaleNotesApi(id, data, tenantId!);

      toast.success("Scale updated");

      await loadScales(customerId);
    } finally {
      setLoading(false);
    }
  };
  
  // ======================================================
  // CREATE CALIBRATION REPORTS
  // ======================================================

  const createCalibrationReports = async (
    scaleIds: string[],
  ) => {
    if (!scaleIds || scaleIds.length === 0) {
      toast.error("Please select at least one scale");
      return false;
    }

    try {
      setLoading(true);

      const res = await createCalibrationReportsApi(
        {
          scaleIds,
        },
        tenantId!,
      );

      if (!res?.success) {
        toast.error(
          res?.message ||
            "Failed to create calibration reports",
        );

        return false;
      }

      const result = res.data;

      const createdCount =
        result?.createdCount ?? 0;

      const skippedCount =
        result?.skippedCount ?? 0;

      const notFoundCount =
        result?.notFoundScaleIds?.length ?? 0;

      if (createdCount > 0) {
        toast.success(
          `${createdCount} calibration report${
            createdCount === 1 ? "" : "s"
          } created.`,
        );
      }

      if (skippedCount > 0) {
        toast(
          `${skippedCount} scale${
            skippedCount === 1 ? "" : "s"
          } already have calibration reports and were skipped.`,
        );
      }

      if (notFoundCount > 0) {
        toast.error(
          `${notFoundCount} scale${
            notFoundCount === 1 ? "" : "s"
          } could not be found.`,
        );
      }

      return true;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to create calibration reports",
      );

      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    scales,
    loading,

    loadScales,
    clearScales,
    deleteScale,
    updateNotes,

    createCalibrationReports,
  };
};
